import * as semaphore from 'semaphore';
import { NgForage } from 'ngforage';
import { SearchSubProjectsInput } from './search-subprojects-input';
import SemaphoreHelper from 'src/app/helpers/semaphore-helper';
import {
  ALL_BOARD_TYPE_SEARCH_PARAMETERS,
  BoardTypeSearchParameter,
  ONLY_BOARD_BOARD_TYPE_SEARCH_PARAMETERS,
  ONLY_IP_BOARD_TYPE_SEARCH_PARAMETERS,
} from './board-type-search-parameter';
import { SubProjectTypeSearchParameter } from './sub-project-type-search-parameter';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { SelectableSearchParameter } from './selectable-search-parameter';
import Company from 'src/app/model/company/company';
import { StringHelper } from 'src/app/helpers/string-helper';

export class SearchSubProjectsInputHelper {
  private constructor() {}

  static readonly INPUT_STORE_KEY = 'search-subprojects-input';
  private static readonly INPUT_STORE_SEMAPHORE = semaphore(1);

  static initializeInput(ngForage: NgForage): Promise<SearchSubProjectsInput> {
    return new Promise((resolve, reject) => {
      SemaphoreHelper.use(this.INPUT_STORE_SEMAPHORE, async () => {
        const toReturn: SearchSubProjectsInput = await ngForage.getItem(
          this.INPUT_STORE_KEY
        );
        resolve(toReturn != null ? toReturn : this.initializeEmptyInput());
      });
    });
  }

  private static initializeEmptyInput(): SearchSubProjectsInput {
    return {
      company: new Company(),
      subProjectTypes: [],
      boardTypeSearchParameters: [],
      noCompletedRepo: true,
      pending: false,
    };
  }

  static resetInput(ngForage: NgForage): SearchSubProjectsInput {
    SemaphoreHelper.use(this.INPUT_STORE_SEMAPHORE, () =>
      ngForage.removeItem(this.INPUT_STORE_KEY)
    );
    return this.initializeEmptyInput();
  }

  static storeInput(ngForage: NgForage, input: SearchSubProjectsInput): void {
    SemaphoreHelper.use(this.INPUT_STORE_SEMAPHORE, async () => {
      // type checking fails without async due to Promise<T> return type of ngForage.setItem
      await ngForage.setItem(this.INPUT_STORE_KEY, input);
    });
  }

  static async storeInputAsync(
    ngForage: NgForage,
    input: SearchSubProjectsInput
  ): Promise<void> {
    await SemaphoreHelper.use(this.INPUT_STORE_SEMAPHORE, async () => {
      await ngForage.setItem(this.INPUT_STORE_KEY, input);
    });
  }

  static getValidBoardTypeChoicesForSubProjectTypes(
    selectedSubProjectTypes: SubProjectTypeSearchParameter[]
  ): SelectableSearchParameter<BoardTypeSearchParameter>[] {
    if (!CollectionHelper.isPopulated(selectedSubProjectTypes)) {
      return ALL_BOARD_TYPE_SEARCH_PARAMETERS;
    }

    let projectSelected = false;
    let ipBoardSelected = false;

    selectedSubProjectTypes.forEach((t) => {
      if (t === SubProjectTypeSearchParameter.PROJECT) {
        projectSelected = true;
      }
      if (t === SubProjectTypeSearchParameter.IPBOARD) {
        ipBoardSelected = true;
      }
    });

    if (!projectSelected && !ipBoardSelected) {
      return [];
    } else if (projectSelected && ipBoardSelected) {
      return ALL_BOARD_TYPE_SEARCH_PARAMETERS;
    } else if (ipBoardSelected) {
      return ONLY_IP_BOARD_TYPE_SEARCH_PARAMETERS;
    } else {
      return ONLY_BOARD_BOARD_TYPE_SEARCH_PARAMETERS;
    }
  }

  static restrictSelectedBoardTypeChoices(
    selectedBoardTypes: BoardTypeSearchParameter[],
    selectedSubProjectTypes: SubProjectTypeSearchParameter[]
  ): void {
    if (
      !CollectionHelper.isPopulated(selectedBoardTypes) ||
      !CollectionHelper.isPopulated(selectedSubProjectTypes)
    ) {
      return;
    }

    let projectSelected = false;
    let ipBoardSelected = false;

    selectedSubProjectTypes.forEach((t) => {
      if (t === SubProjectTypeSearchParameter.PROJECT) {
        projectSelected = true;
      }
      if (t === SubProjectTypeSearchParameter.IPBOARD) {
        ipBoardSelected = true;
      }
    });

    if (!projectSelected && !ipBoardSelected) {
      selectedBoardTypes.length = 0;
    } else if (projectSelected && ipBoardSelected) {
      return;
    } else if (ipBoardSelected) {
      CollectionHelper.filterInPlace(selectedBoardTypes, (e) =>
        ONLY_IP_BOARD_TYPE_SEARCH_PARAMETERS.some((p) => p.key === e)
      );
    } else {
      CollectionHelper.filterInPlace(selectedBoardTypes, (e) =>
        ONLY_BOARD_BOARD_TYPE_SEARCH_PARAMETERS.some((p) => p.key === e)
      );
    }
  }

  static inputIsValid(searchInput: SearchSubProjectsInput): boolean {
    return (
      searchInput != null &&
      (searchInput.projectId != null ||
        (searchInput.company != null && searchInput.company.id != null) ||
        (searchInput.contact != null && searchInput.contact.id != null) ||
        !StringHelper.isEmpty(searchInput.street) ||
        !StringHelper.isEmpty(searchInput.number) ||
        !StringHelper.isEmpty(searchInput.city) ||
        !StringHelper.isEmpty(searchInput.postalCode) ||
        CollectionHelper.isPopulated(searchInput.subProjectTypes) ||
        CollectionHelper.isPopulated(searchInput.boardTypeSearchParameters) ||
        searchInput.noCompletedRepo ||
        searchInput.pending)
    );
  }
}
